/**
 * Employee absence hooks using TanStack Query
 * Provides data fetching, caching, and mutations for employee absence operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { EmployeeAbsencesService } from '@/api/services/EmployeeAbsencesService';
import { employee_absences_CreateRecurringAbsenceRequest } from '@/api/models/employee_absences_CreateRecurringAbsenceRequest';
import { toEnumMember } from '@/lib/enumMember';
import { shiftKeys } from '@/hooks/useShifts';
import { workforceKeys } from '@/hooks/useWorkforce';
import type {
  EmployeeAbsence,
  EmployeeAbsenceListParams,
  CreateEmployeeAbsenceRequest,
  UpdateEmployeeAbsenceRequest,
  UpdateRecurringAbsenceRequest,
  CertificateDisposition,
  PendingAbsenceParams,
} from '@/types/employeeAbsences';
import { ApiError } from '@/api';
import { toast } from 'sonner';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import { NAV_BADGE_POLL_MS, visiblePoll } from '@/lib/polling';

// Query keys for employee absences
export const employeeAbsenceKeys = {
  all: ['employee-absences'] as const,
  lists: () => [...employeeAbsenceKeys.all, 'list'] as const,
  list: (params: EmployeeAbsenceListParams) =>
    [...employeeAbsenceKeys.lists(), params] as const,
  details: () => [...employeeAbsenceKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeeAbsenceKeys.details(), id] as const,
  patterns: () => [...employeeAbsenceKeys.all, 'patterns'] as const,
  patternList: (params: { employeeId?: string; limit?: number; offset?: number }) =>
    [...employeeAbsenceKeys.patterns(), params] as const,
  patternDetail: (id: string) => [...employeeAbsenceKeys.patterns(), id] as const,
  pending: () => [...employeeAbsenceKeys.all, 'pending'] as const,
  pendingList: (params: PendingAbsenceParams) =>
    [...employeeAbsenceKeys.pending(), 'list', params] as const,
  pendingCount: (params: { forMe?: boolean } = {}) =>
    [...employeeAbsenceKeys.pending(), 'count', { forMe: params.forMe ?? false }] as const,
  certificates: (absenceId: string) =>
    [...employeeAbsenceKeys.all, 'certificates', absenceId] as const,
};

/** Report error codes whose translated text replaces the generic absence toast. */
const REPORT_ERROR_CODES: ReadonlySet<string> = new Set([
  'ABSENCE_REPORT_TOO_OLD',
  'ABSENCE_REPORT_NOT_ACTIVE',
  'ABSENCE_REPORT_CONFLICT',
  'ABSENCE_NOT_SELF_REPORT',
  // 403 on approve/reject/acknowledge/cancel-report/eau-retrieved of one's own absence.
  'ABSENCE_SELF_DECISION',
  'ACCIDENT_FIELDS_REQUIRED',
  'CERTIFICATE_MODE_NOT_EAU',
  'CERTIFICATE_LOCKED',
  'CERTIFICATE_IMAGE_UNPROCESSABLE',
  'CERTIFICATE_STORAGE_UNAVAILABLE',
]);

const absenceErrorFallback = msg({
  id: 'absences.toast.error',
  message: 'Error performing absence operation',
});

/** Toast for the report and certificate actions: the code's own text, else the generic one. */
function toastReportError(error: unknown, i18n: I18n): void {
  toast.error(
    translateBackendError(extractBackendError(error).code, i18n, absenceErrorFallback)
  );
}

/**
 * Error handler result indicating which toast to show
 */
type AbsenceErrorToastId =
  | 'overlapError'
  | 'optimisticLockError'
  | 'notFoundError'
  | 'invalidTypeError'
  | 'invalidDatesError'
  | 'invalidPartialDayError'
  | 'error'
  | 'deleteError';

/**
 * Centralized error handler for absence mutations.
 * Uses ApiError.status for HTTP status code checking, with message-based
 * fallback for validation-specific errors not represented by status codes.
 */
function handleAbsenceError(
  error: Error,
  i18n: I18n,
  context: 'create' | 'update' | 'delete'
): void {
  // A report error (#1767) carries a code with its own text; without this an
  // ABSENCE_REPORT_CONFLICT (409) would read as the overlap toast below.
  const code = extractBackendError(error).code;
  if (code && REPORT_ERROR_CODES.has(code)) {
    toastReportError(error, i18n);
    return;
  }

  const lowerMessage = (error.message || '').toLowerCase();

  // Use ApiError.status for HTTP status code detection
  if (error instanceof ApiError) {
    // 409 Conflict - could be overlap or optimistic lock
    if (error.status === 409) {
      // Check message to distinguish between overlap and optimistic lock
      if (
        lowerMessage.includes('modified by another user') ||
        lowerMessage.includes('optimistic')
      ) {
        showAbsenceToast(i18n, 'optimisticLockError');
        return;
      }
      // Default 409 to overlap error
      showAbsenceToast(i18n, 'overlapError');
      return;
    }

    // 404 Not Found
    if (error.status === 404) {
      showAbsenceToast(i18n, 'notFoundError');
      return;
    }
  }

  // Message-based checks for validation errors (backend validation messages)
  // These handle both ApiError body messages and regular Error messages with status prefixes
  if (lowerMessage.includes('overlap')) {
    showAbsenceToast(i18n, 'overlapError');
    return;
  }

  // Handle optimistic lock error via message (for cases where ApiError is not available)
  if (
    lowerMessage.includes('modified by another user') ||
    lowerMessage.includes('optimistic')
  ) {
    showAbsenceToast(i18n, 'optimisticLockError');
    return;
  }

  // Handle not found error via message (for cases where ApiError is not available)
  if (lowerMessage.includes('not found')) {
    showAbsenceToast(i18n, 'notFoundError');
    return;
  }

  if (lowerMessage.includes('invalid absence type')) {
    showAbsenceToast(i18n, 'invalidTypeError');
    return;
  }

  if (lowerMessage.includes('invalid absence dates')) {
    showAbsenceToast(i18n, 'invalidDatesError');
    return;
  }

  if (lowerMessage.includes('partial-day requires valid time range')) {
    showAbsenceToast(i18n, 'invalidPartialDayError');
    return;
  }

  // Generic error fallback based on context
  const fallbackId: AbsenceErrorToastId =
    context === 'delete' ? 'deleteError' : 'error';
  showAbsenceToast(i18n, fallbackId);
}

/**
 * Helper to show a toast with the translated message.
 * Uses a switch statement with literal i18n calls so Lingui can extract messages.
 */
function showAbsenceToast(i18n: I18n, errorType: AbsenceErrorToastId): void {
  let message: string;

  switch (errorType) {
    case 'overlapError':
      message = i18n._({
        id: 'absences.toast.overlapError',
        message: 'Absence overlaps with existing absence',
      });
      break;
    case 'optimisticLockError':
      message = i18n._({
        id: 'absences.toast.optimisticLockError',
        message: 'This absence was modified by another user. Please refresh.',
      });
      break;
    case 'notFoundError':
      message = i18n._({
        id: 'absences.toast.notFoundError',
        message: 'Absence not found',
      });
      break;
    case 'invalidTypeError':
      message = i18n._({
        id: 'absences.toast.invalidTypeError',
        message: 'Invalid absence type',
      });
      break;
    case 'invalidDatesError':
      message = i18n._({
        id: 'absences.toast.invalidDatesError',
        message: 'Invalid absence dates',
      });
      break;
    case 'invalidPartialDayError':
      message = i18n._({
        id: 'absences.toast.invalidPartialDayError',
        message: 'Partial-day absences require valid start and end times',
      });
      break;
    case 'deleteError':
      message = i18n._({
        id: 'absences.toast.deleteError',
        message: 'Failed to delete absence',
      });
      break;
    case 'error':
    default:
      message = i18n._({
        id: 'absences.toast.error',
        message: 'Error performing absence operation',
      });
      break;
  }

  toast.error(message);
}

/**
 * Hook to fetch paginated employee absences with filters.
 *
 * @param params - Filter parameters for absences query
 * @returns TanStack Query result with absence list data
 */
export function useEmployeeAbsences(params: EmployeeAbsenceListParams = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: employeeAbsenceKeys.list(params),
    queryFn: () =>
      EmployeeAbsencesService.getApiV1EmployeeAbsences(
        params.employeeId,
        params.absenceType,
        // Report filters (#1767). The generated client takes them positionally
        // and omits an undefined value from the query string, so each one is
        // sent only when set.
        params.approvalStatus,
        params.isSelfReport,
        params.acknowledged,
        params.certificateStatus,
        params.fromDate,
        params.toDate,
        params.limit ?? 100,
        params.offset
      ),
    enabled: isSignedIn && isAuthReady,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on authentication/authorization errors
      if (error instanceof ApiError) {
        if (error.status === 401 || error.status === 403) {
          return false;
        }
      }
      // Fallback for non-ApiError errors: check message strings
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('Unauthorized'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch a single employee absence by ID.
 *
 * @param id - Absence ID
 * @param options - Optional query options
 * @returns TanStack Query result with absence data
 */
export function useEmployeeAbsence(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: employeeAbsenceKeys.detail(id),
    queryFn: () => EmployeeAbsencesService.getApiV1EmployeeAbsences1(id),
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady,
    staleTime: 30000, // 30 seconds
  });
}

/** Extended request type that includes submit_as_request for the approval workflow */
export type CreateAbsenceWithApprovalRequest = CreateEmployeeAbsenceRequest & {
  submit_as_request?: boolean;
};

/**
 * Hook to create a new employee absence.
 *
 * @returns Mutation function and state
 */
export function useCreateEmployeeAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: CreateAbsenceWithApprovalRequest) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsences(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      toast.success(
        i18n._({
          id: 'absences.toast.createSuccess',
          message: 'Absence created successfully',
        })
      );
    },
    onError: (error: Error) => handleAbsenceError(error, i18n, 'create'),
  });
}

export interface UpdateEmployeeAbsenceOptions {
  /**
   * Backend error codes the caller shows inline itself. For these the hook
   * shows no toast (the message would appear twice); the caller refetches.
   */
  inlineErrorCodes?: ReadonlySet<string>;
}

/**
 * Hook to update an existing employee absence.
 *
 * @returns Mutation function and state
 */
export function useUpdateEmployeeAbsence(options: UpdateEmployeeAbsenceOptions = {}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const { inlineErrorCodes } = options;

  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: UpdateEmployeeAbsenceRequest;
    }) => EmployeeAbsencesService.putApiV1EmployeeAbsences(id, request),
    onSuccess: (data: EmployeeAbsence, variables) => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.setQueryData(employeeAbsenceKeys.detail(variables.id), data);
      toast.success(
        i18n._({
          id: 'absences.toast.updateSuccess',
          message: 'Absence updated successfully',
        })
      );
    },
    onError: (error: Error) => {
      const code = extractBackendError(error).code;
      if (code && inlineErrorCodes?.has(code)) {
        return;
      }
      handleAbsenceError(error, i18n, 'update');
    },
  });
}

/**
 * Hook to delete an employee absence.
 *
 * @returns Mutation function and state
 */
export function useDeleteEmployeeAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) =>
      EmployeeAbsencesService.deleteApiV1EmployeeAbsences(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.removeQueries({ queryKey: employeeAbsenceKeys.detail(id) });
      toast.success(
        i18n._({
          id: 'absences.toast.deleteSuccess',
          message: 'Absence deleted successfully',
        })
      );
    },
    onError: (error: Error) => handleAbsenceError(error, i18n, 'delete'),
  });
}

// =====================================================
// Recurring absence hooks
// =====================================================

// The generated request declares `absence_type` / `recurrence_type` as TypeScript
// string enums, which are nominal: a value-identical string literal off the form
// is not assignable to them. `toEnumMember` crosses that boundary with a real
// runtime check; an unknown value throws rather than silently sending
// `undefined` for a field the API requires.
function toAbsenceTypeEnum(
  value: string
): employee_absences_CreateRecurringAbsenceRequest.absence_type {
  const member = toEnumMember(
    employee_absences_CreateRecurringAbsenceRequest.absence_type,
    value
  );
  if (member === undefined) {
    throw new Error(`Unsupported absence type: ${value}`);
  }
  return member;
}

function toRecurrenceTypeEnum(
  value: string
): employee_absences_CreateRecurringAbsenceRequest.recurrence_type {
  const member = toEnumMember(
    employee_absences_CreateRecurringAbsenceRequest.recurrence_type,
    value
  );
  if (member === undefined) {
    throw new Error(`Unsupported recurrence type: ${value}`);
  }
  return member;
}

/** Request type for creating a recurring absence */
export interface CreateRecurringAbsenceRequest {
  employee_id: string;
  absence_type: string;
  recurrence_type: string;
  recurrence_days: number[];
  recurrence_interval: number;
  full_day: boolean;
  from_time?: string;
  to_time?: string;
  start_date: string;
  end_date?: string;
  notes?: string;
  submit_as_request?: boolean;
}

/** Response type for absence pattern */
export interface AbsencePattern {
  id: string;
  employee_id: string;
  absence_type: string;
  recurrence_type: string;
  recurrence_days: number[];
  recurrence_interval: number;
  full_day: boolean;
  from_time?: string;
  to_time?: string;
  start_date: string;
  end_date?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
}

/** Response type for pattern list */
export interface AbsencePatternListResponse {
  patterns: AbsencePattern[];
  total_count: number;
  limit: number;
  offset: number;
}

/** Response type for recurring absence creation */
export interface RecurringAbsenceCreatedResponse {
  pattern: AbsencePattern;
  absences: EmployeeAbsence[];
  count: number;
}

/** Response type for pending count */
export interface PendingAbsenceCountResponse {
  count: number;
}

/**
 * Hook to create a recurring absence.
 *
 * @returns Mutation function and state
 */
export function useCreateRecurringAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (request: CreateRecurringAbsenceRequest) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsencesRecurring({
        ...request,
        absence_type: toAbsenceTypeEnum(request.absence_type),
        recurrence_type: toRecurrenceTypeEnum(request.recurrence_type),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.patterns() });
      toast.success(
        i18n._({
          id: 'absences.toast.recurringCreateSuccess',
          message: 'Recurring absence created successfully',
        })
      );
    },
    onError: (error: Error) => handleAbsenceError(error, i18n, 'create'),
  });
}

/**
 * Hook to update a recurring absence pattern.
 *
 * @returns Mutation function and state
 */
export function useUpdateRecurringAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      patternId,
      request,
    }: {
      patternId: string;
      request: UpdateRecurringAbsenceRequest;
    }) =>
      EmployeeAbsencesService.putApiV1EmployeeAbsencesPatterns(
        patternId,
        request
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: employeeAbsenceKeys.patterns(),
      });
      toast.success(
        i18n._({
          id: 'absences.toast.recurringUpdateSuccess',
          message: 'Recurring absence updated successfully',
        })
      );
    },
    onError: (error: Error) => handleAbsenceError(error, i18n, 'update'),
  });
}

/**
 * Hook to fetch a single absence pattern by ID.
 *
 * @param patternId - Pattern ID
 * @param options - Optional query options
 * @returns TanStack Query result with pattern data
 */
export function useAbsencePattern(patternId: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: employeeAbsenceKeys.patternDetail(patternId),
    queryFn: () =>
      EmployeeAbsencesService.getApiV1EmployeeAbsencesPatterns1(patternId),
    enabled: (options?.enabled ?? true) && !!patternId && isSignedIn && isAuthReady,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch absence patterns with optional employee filter.
 */
export function useAbsencePatterns(params: {
  employeeId?: string;
  limit?: number;
  offset?: number;
} = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: employeeAbsenceKeys.patternList(params),
    queryFn: () =>
      EmployeeAbsencesService.getApiV1EmployeeAbsencesPatterns(
        params.employeeId,
        params.limit,
        params.offset
      ),
    enabled: isSignedIn && isAuthReady,
    staleTime: 30000,
  });
}

/**
 * Hook to delete a recurring absence pattern.
 */
export function useDeleteRecurringAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ patternId, scope = 'all' }: { patternId: string; scope?: 'all' | 'future' }) =>
      EmployeeAbsencesService.deleteApiV1EmployeeAbsencesPatterns(patternId, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.patterns() });
      toast.success(
        i18n._({
          id: 'absences.toast.recurringDeleteSuccess',
          message: 'Recurring absence deleted successfully',
        })
      );
    },
    onError: (error: Error) => handleAbsenceError(error, i18n, 'delete'),
  });
}

// =====================================================
// Approval workflow hooks
// =====================================================

/**
 * Hook to approve an absence request.
 */
export function useApproveAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (absenceId: string) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsencesApprove(absenceId),
    onSuccess: (_data, absenceId) => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.pending() });
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.detail(absenceId) });
      // Approving an absence may auto-cancel overlapping planned shifts and
      // recompute the Zeitkonto on the backend, so refresh those caches too.
      queryClient.invalidateQueries({ queryKey: shiftKeys.all });
      queryClient.invalidateQueries({ queryKey: workforceKeys.all });
      toast.success(
        i18n._({
          id: 'absences.approval.approveSuccess',
          message: 'Absence approved',
        })
      );
    },
    onError: (error: Error) => {
      const lowerMessage = (error.message || '').toLowerCase();
      if (lowerMessage.includes('not in pending state')) {
        toast.error(
          i18n._({
            id: 'absences.approval.notPendingError',
            message: 'Absence is not in pending state',
          })
        );
        return;
      }
      handleAbsenceError(error, i18n, 'update');
    },
  });
}

/**
 * Hook to reject an absence request.
 */
export function useRejectAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ absenceId, reason }: { absenceId: string; reason: string }) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsencesReject(absenceId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.pending() });
      toast.success(
        i18n._({
          id: 'absences.approval.rejectSuccess',
          message: 'Absence rejected',
        })
      );
    },
    onError: (error: Error) => handleAbsenceError(error, i18n, 'update'),
  });
}

/**
 * Hook to cancel an absence request.
 */
export function useCancelAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (absenceId: string) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsencesCancel(absenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.pending() });
      toast.success(
        i18n._({
          id: 'absences.toast.cancelSuccess',
          message: 'Absence request cancelled',
        })
      );
    },
    onError: (error: Error) => handleAbsenceError(error, i18n, 'update'),
  });
}

/**
 * Hook to fetch pending absence requests.
 *
 * `forMe` (#1767) limits the queue to the items routed to the caller as
 * approver; it is part of the query key and sent as `for_me=true` only when set.
 */
export function usePendingAbsences(params: PendingAbsenceParams = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: employeeAbsenceKeys.pendingList(params),
    queryFn: () =>
      EmployeeAbsencesService.getApiV1EmployeeAbsencesPending(
        params.limit,
        params.offset,
        params.forMe ? true : undefined
      ),
    enabled: isSignedIn && isAuthReady,
    staleTime: 15000, // 15 seconds - shorter for approval queue
  });
}

/**
 * Hook to fetch count of pending absence requests.
 *
 * `forMe` must match the list's so the badge counts the same set.
 */
export function useCountPendingAbsences(params: { forMe?: boolean; enabled?: boolean } = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const forMe = params.forMe ?? false;

  return useQuery({
    queryKey: employeeAbsenceKeys.pendingCount({ forMe }),
    queryFn: () =>
      EmployeeAbsencesService.getApiV1EmployeeAbsencesPendingCount(
        forMe ? true : undefined
      ),
    enabled: (params.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 15000,
    ...visiblePoll(NAV_BADGE_POLL_MS),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// =====================================================
// Office actions on self-reported absences (#1767)
// =====================================================

/**
 * Refresh everything a report action can change: the lists, the approval
 * queue, the row itself, and the shift and Zeitkonto caches (cancel-report
 * recomputes the Zeitkonto on the backend), like approve.
 */
function invalidateAfterReportAction(
  queryClient: ReturnType<typeof useQueryClient>,
  absenceId: string
): void {
  queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
  queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.pending() });
  queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.detail(absenceId) });
  queryClient.invalidateQueries({ queryKey: shiftKeys.all });
  queryClient.invalidateQueries({ queryKey: workforceKeys.all });
}

/**
 * Acknowledge a self-reported absence (`POST /employee-absences/:id/acknowledge`).
 */
export function useAcknowledgeAbsence() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (absenceId: string) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsencesAcknowledge(absenceId),
    onSuccess: (_data, absenceId) => {
      invalidateAfterReportAction(queryClient, absenceId);
      toast.success(
        i18n._({ id: 'absences.toast.acknowledged', message: 'Report acknowledged' })
      );
    },
    onError: (error: unknown) => toastReportError(error, i18n),
  });
}

/**
 * Cancel a self-reported absence with a reason
 * (`POST /employee-absences/:id/cancel-report`). Cancelled shifts are not restored.
 */
export function useCancelAbsenceReport() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsencesCancelReport(id, {
        // Trimmed at the boundary so whitespace collapses to "" and the backend rejects it.
        reason: reason.trim(),
      }),
    onSuccess: (_data, variables) => {
      invalidateAfterReportAction(queryClient, variables.id);
      toast.success(
        i18n._({ id: 'absences.toast.reportCancelled', message: 'Report cancelled' })
      );
    },
    onError: (error: unknown) => toastReportError(error, i18n),
  });
}

/**
 * Record that the office retrieved the electronic certificate (eAU) of a report
 * (`POST /employee-absences/:id/eau-retrieved`; EAU mode only).
 */
export function useMarkEauRetrieved() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (absenceId: string) =>
      EmployeeAbsencesService.postApiV1EmployeeAbsencesEauRetrieved(absenceId),
    onSuccess: (_data, absenceId) => {
      invalidateAfterReportAction(queryClient, absenceId);
      toast.success(
        i18n._({ id: 'absences.toast.eauRetrieved', message: 'eAU marked as retrieved' })
      );
    },
    onError: (error: unknown) => toastReportError(error, i18n),
  });
}

/**
 * List the certificates of an absence (office view, metadata only, no URL).
 * Every fetch writes an access-log row on the backend, so it runs only while
 * `enabled` (the viewer dialog is open) and is never kept warm.
 */
export function useAbsenceCertificates(absenceId: string, enabled: boolean) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: employeeAbsenceKeys.certificates(absenceId),
    queryFn: () => EmployeeAbsencesService.getApiV1EmployeeAbsencesCertificates(absenceId),
    enabled: enabled && !!absenceId && !!isSignedIn && isAuthReady,
    staleTime: 0,
    gcTime: 0,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Fetch a fresh 5-minute URL for one certificate. A mutation on purpose: the
 * signed URL expires and every request is logged, so it is never cached.
 */
export function useAbsenceCertificateDownload() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({
      absenceId,
      imageId,
      disposition,
    }: {
      absenceId: string;
      imageId: string;
      disposition: CertificateDisposition;
    }) =>
      EmployeeAbsencesService.getApiV1EmployeeAbsencesCertificatesDownload(
        absenceId,
        imageId,
        disposition
      ),
    onError: (error: unknown) => toastReportError(error, i18n),
  });
}
